import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaHotel, FaUser, FaIdCard, FaCheckCircle, FaExclamationCircle, FaFileAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import "./HotelOwners.css";

import { getFileUrl } from "../utils/imageUrl";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function HotelOwnerDetailsPage() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await fetch(`${API}/admin/hotel-owners/${ownerId}`, {
          headers: getAuthHeaders(),
        });
        const json = await res.json();
        if (res.ok) {
          setData(json);
        } else {
          setError(json.message || "Failed to load details");
        }
      } catch (e) {
        console.error(e);
        setError("Network error");
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, [ownerId]);

  const handleActivate = async () => {
    try {
      const result = await Swal.fire({
        title: "Activate Account?",
        text: "This will approve the hotel owner and send an activation email.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Activate",
        confirmButtonColor: "#10b981",
      });

      if (!result.isConfirmed) return;

      const res = await fetch(`${API}/admin/hotel-owners/${ownerId}/status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Activated!", "The account has been activated and an email was sent.", "success");
        navigate("/admin/hotel-owners");
      } else {
        throw new Error(resData.message || "Failed to activate");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const handleIssue = async () => {
    try {
      const { value: formValues } = await Swal.fire({
        title: "Report an Issue",
        html: `
          <div style="text-align: left; margin-bottom: 10px;">
            <label style="display: block; font-size: 14px; font-weight: bold; margin-bottom: 5px;">Issue Category</label>
            <select id="swal-category" class="swal2-select" style="width: 100%; display: flex; margin: 0; font-size: 14px; padding: 10px;">
              <option value="Property Information">Property Information</option>
              <option value="Owner Details">Owner Details</option>
              <option value="Verification Documents">Verification Documents</option>
            </select>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Send Email",
        confirmButtonColor: "#f97316",
        preConfirm: () => {
          const category = document.getElementById("swal-category").value;
          return { category, issueDetails: "Please review and correct the information in this section." };
        }
      });

      if (!formValues) return;

      const res = await fetch(`${API}/admin/hotel-owners/${ownerId}/issue`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formValues),
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Done!", resData.message, "success");
        navigate("/admin/hotel-owners");
      } else {
        throw new Error(resData.message || "Failed to report issue");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  if (loading) return <div className="owners-page"><div className="premium-glass-card">Loading...</div></div>;
  if (error) return <div className="owners-page"><div className="premium-glass-card" style={{ color: 'crimson' }}>{error}</div></div>;
  if (!data) return <div className="owners-page"><div className="premium-glass-card">Not found.</div></div>;

  const docUrl = (path) => getFileUrl(path, API);



  return (
    <div className="owners-page">
      <div className="owners-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <button onClick={() => navigate(-1)} className="badge" style={{ marginBottom: '10px', background: '#fff', border: '1px solid #ddd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FaArrowLeft /> Back to List
          </button>
          <h2 className="owners-title">Owner Registration Details</h2>
          <p className="muted">Review documents and information for HO-{String(data.id).padStart(6, '0')}</p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleActivate} className="badge" style={{ background: '#10b981', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaCheckCircle /> Activate Account
          </button>
          <button onClick={handleIssue} className="badge" style={{ background: '#f97316', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaExclamationCircle /> Report Issue
          </button>
        </div>
      </div>

      <div className="card table-card" style={{ padding: '30px', marginTop: '20px' }}>
        <div className="detail-section">
          <h4><FaHotel /> Property Information</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Hotel Name:</label> <span>{data.hotel_name || data.business_name}</span></div>
            <div className="detail-item"><label>Property Type:</label> <span>{data.property_type || "Hotel"}</span></div>
            <div className="detail-item"><label>Address:</label> <span>{data.hotel_address}</span></div>
            <div className="detail-item"><label>Zip Code:</label> <span>{data.hotel_zip || "N/A"}</span></div>
          </div>
        </div>

        <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />

        <div className="detail-section">
          <h4><FaUser /> Owner Details</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Full Name:</label> <span>{data.full_name}</span></div>
            <div className="detail-item"><label>Email Address:</label> <span>{data.email}</span></div>
            <div className="detail-item"><label>Phone Number:</label> <span>{data.phone_number}</span></div>
            <div className="detail-item"><label>NIC / ID Number:</label> <span>{data.national_id_number || "N/A"}</span></div>
            <div className="detail-item"><label>Date of Birth:</label> <span>{data.dob ? new Date(data.dob).toLocaleDateString() : "N/A"}</span></div>
          </div>
        </div>

        <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />

        <div className="detail-section documents">
          <h4><FaIdCard /> Verification Documents</h4>
          <div className="document-grid">
            <DocItem label="NIC Front" url={docUrl(data.nic_or_driving_licence_front)} />
            <DocItem label="NIC Back" url={docUrl(data.nic_or_driving_licence_back)} />
            <DocItem label="Utility Bill Front" url={docUrl(data.water_or_light_bill_front)} />
            <DocItem label="Utility Bill Back" url={docUrl(data.water_or_light_bill_back)} />
            <DocItem
              label="Business Reg / Quals"
              url={docUrl(data.business_registration_or_qualifications || data.business_registration_doc)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function DocItem({ label, url }) {
  const [imgUrl, setImgUrl] = useState(url);
  const isPdf = url && url.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    setImgUrl(url);
  }, [url]);

  const handleImageError = () => {
    if (!imgUrl) {
      setImgUrl(null);
      return;
    }
    // If /uploads/ failed, try /api/uploads/
    if (imgUrl.includes('/uploads/') && !imgUrl.includes('/api/uploads/')) {
      const altUrl = imgUrl.replace('/uploads/', '/api/uploads/');
      if (altUrl !== imgUrl) {
        setImgUrl(altUrl);
        return;
      }
    }
    // If it had extra subdirectory segments, try root uploads
    if (imgUrl.includes('/uploads/') && imgUrl.split('/').length > 5) {
      const parts = imgUrl.split('/');
      const fileName = parts[parts.length - 1];
      const baseUrl = imgUrl.substring(0, imgUrl.indexOf('/uploads/') + 9);
      const rootUrl = `${baseUrl}${fileName}`;
      if (rootUrl !== imgUrl) {
        setImgUrl(rootUrl);
        return;
      }
    }
    setImgUrl(null);
  };

  if (!imgUrl) return (
    <div className="doc-item">
      <label>{label}</label>
      <div className="doc-preview" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '12px', background: '#f8fafc', textAlign: 'center', padding: '10px' }}>
        <FaExclamationCircle style={{ fontSize: '24px', marginBottom: '5px', color: '#ccc' }} />
        <span>Image Not Found</span>
      </div>
    </div>
  );

  return (
    <div className="doc-item">
      <label>{label}</label>
      <div className="doc-preview" onClick={() => window.open(imgUrl, '_blank')} style={{ position: 'relative' }}>
        {isPdf ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff' }}>
            <FaFileAlt style={{ fontSize: '30px', color: '#f97316', marginBottom: '5px' }} />
            <span style={{ fontSize: '11px', color: '#666' }}>View PDF</span>
          </div>
        ) : (
          <img
            src={imgUrl}
            alt={label}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={handleImageError}
          />
        )}
      </div>
    </div>
  );
}

